import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Typography } from "@mui/material"
import { getLostFoundCaseStatus } from "api"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { BulbIcon, Mobile, useIsDesktop } from "ui"
import {
  applicationRequestSuccess,
  extractProperty,
  replaceRichTextLinks,
  useFocusOnError,
} from "utils"
import { commonSchema } from "utils/FormSchema"
import * as yup from "yup"
import {
  CustomTextField,
  NumbersController,
  prefixOptionsArray,
} from "./Common"

type Props = {
  lostAndFoundStatus: any
  locale: any
  label: string
}

const Layout = styled.div`
  padding-top: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 16px;
  }
`
const StyledMain = styled.div`
  box-shadow: 0px 2px 8px 0px #26303d29;
  padding: 24px;
`
const StyledTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`

const StyledLine = styled.div`
  width: 1px;
  background-color: #d1d5db;
  height: 24px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    display: none;
  }
`

const StyledTipTextWrapper = styled.div`
  background-color: #1bbd7714;
  padding: 21px 12px 21px 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: fit-content;
  border-radius: 12px;
  p {
    margin: 0px;
  }
  margin-top: 16px;
  margin-bottom: 16px;
  svg {
    min-width: 32px;
    min-height: 32px;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    margin-top: 32px;
    margin-bottom: 0px;
  }
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 32px;
    padding-top: 32px;
  }
  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-top: 16px;
  }
  .MuiFormControl-root {
    ${({ theme }) => theme.breakpoints.down("md")} {
      margin-block-end: 8px;
    }
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 56px;
`
const StyledResponseWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-start;
  border-top: 1px solid #d1d5db;
  padding-top: 32px;
  margin-top: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 40px;
    margin-top: 0px;
  }
`

const StyledDot = styled.div<{ color?: string }>`
  min-width: 15px;
  min-height: 15px;
  border-radius: 200px;
  border: 3px solid white;
  background-color: ${(props) => props.color};
  box-shadow: 0px 0px 16px 0px ${(props) => props.color};
  margin-top: 5px;
`

const LostStatusCheck = ({ lostAndFoundStatus, locale, label }: Props) => {
  const title = extractProperty(lostAndFoundStatus, "title")
  const tipTexts = extractProperty(lostAndFoundStatus, "tipTexts")
  const [response, setResponse] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  type FormData = yup.InferType<typeof schema>
  const t = useTranslations("Forms")
  const schema = yup.object({
    caseNumber: commonSchema.common(locale).caseNumber,
    cellphone: commonSchema.common(locale).phonePrefix,
    phone: commonSchema.common(locale).phoneLostStatus,
  })

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cellphone: prefixOptionsArray[0].value,
    },
  })

  // Add focus management for validation errors
  useFocusOnError(errors, isSubmitted)

  const getStatusColor = (statusId: number): string => {
    switch (statusId) {
      case 0:
        return "#E00700"
      case 1:
        return "#F8A401" // Orange
      case 2:
        return "#1BBD77"
      case 3:
        return "#E00700" // Red
      case 4:
        return "#1BBD77" // Green
      case 5:
        return "#6F747D"
      case 6:
        return "#6F747D" // Grey
      default:
        return "#6B7280" // Default grey
    }
  }

  const onSubmit = async (data: FormData) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setError(null)
    setResponse(null)
    try {
      const res = await getLostFoundCaseStatus(
        Number(data.caseNumber),
        Number(data.cellphone + data.phone),
        locale,
      )
      if (res?.data?.errorCode) {
        setError(t("statusNotFound"))
        return
      }
      const body = res?.data?.body
      if (
        !body ||
        body.statusId == null ||
        body.statusId === 0 ||
        !body.statusText ||
        /^[A-Za-z]+$/.test(body.statusText)
      ) {
        setError(t("statusNotFound"))
        return
      }
      applicationRequestSuccess("בדיקת סטטוס", label)
      setResponse(body)
    } catch {
      setError(t("statusNotFound"))
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDesktop = useIsDesktop()

  return (
    <Layout>
      <StyledMain>
        <StyledTextWrapper>
          <Typography variant="h4">{title}</Typography>

          <StyledLine />

          {isDesktop && (
            <Typography variant="body3">
              {locale === "he" ? (
                <>
                  שדה חובה
                  <span style={{ color: "rgb(224, 7, 0)" }}>*</span>
                </>
              ) : (
                <>
                  Mandatory field
                  <span style={{ color: "rgb(224, 7, 0)" }}>*</span>
                </>
              )}
            </Typography>
          )}
        </StyledTextWrapper>
        <StyledTipTextWrapper>
          <BulbIcon />
          <Typography
            variant="body2"
            component={"div"}
            dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(tipTexts) }}
          />
        </StyledTipTextWrapper>
        <Mobile>
          <Typography variant="body3">
            {locale === "he" ? (
              <>
                שדה חובה
                <span style={{ color: "rgb(224, 7, 0)" }}>*</span>
              </>
            ) : (
              <>
                Mandatory field
                <span style={{ color: "rgb(224, 7, 0)" }}>*</span>
              </>
            )}
          </Typography>
        </Mobile>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <CustomTextField
            name="caseNumber"
            control={control}
            label={t("caseNumber")}
            required={true}
            isSubmitted={isSubmitted}
            errors={errors}
          />
          <NumbersController
            phoneName="phone"
            prefixName="cellphone"
            control={control}
            label={t("mobileNumber")}
            required={true}
            isSubmitted={isSubmitted}
            errors={errors}
            locale={locale}
          />
          <StyledButton variant="primary" type="submit" disabled={isSubmitting}>
            {locale === "he" ? "בדיקת סטטוס" : "Check Status"}
          </StyledButton>
        </FormContainer>
        {error && (
          <StyledResponseWrapper>
            <StyledDot color="#E00700" />
            <Typography variant="body1" style={{ fontWeight: 700 }}>
              {error}
            </Typography>
          </StyledResponseWrapper>
        )}
        {response && (
          <StyledResponseWrapper>
            <StyledDot color={getStatusColor(response?.statusId)} />
            <Typography variant="body1" style={{ fontWeight: 700 }}>
              {response?.statusText}
            </Typography>
          </StyledResponseWrapper>
        )}
      </StyledMain>
    </Layout>
  )
}

export default LostStatusCheck
